import React, { useState } from 'react';
import { Button, DropdownMenu, SegmentedControl, SparkIcon } from '@spark';
import {
    AGGREGATIONS,
    ALL_ENTITY_TYPES,
    CONSTRAINT_TYPES,
    EVALUATION_METRICS,
    LOAD_PROFILES,
    MATCHING_OPTIONS,
    OPERATORS,
    PERFORMANCE_METRICS,
    PRICING_METRICS,
    QUALITY_VALUE_TYPES,
    RESOURCE_METRICS,
    metricsForType,
    retypeConstraint,
    unusedMetricsForType,
} from './constraintOptions';

/**
 * Labeled single-select built on the Spark DropdownMenu. `width` pins the
 * field to a fixed Figma width; `flex` lets it fill the remaining row space.
 */
const SelectField = ({ label, options, value, onChange, width, flex = false, required = true }) => {
    const [open, setOpen] = useState(false);
    const selected = options.find((o) => o.value === value);
    const style = flex ? { flex: '1 1 0', minWidth: 100 } : { width, flexShrink: 0 };
    return (
        <div className="cw-constraint__field" style={style}>
            {label && (
                <div className="cw-constraint__field-label">
                    {label}
                    {required && <span className="cw-required-asterisk">*</span>}
                </div>
            )}
            <DropdownMenu
                selectType="single-select"
                showLabel={false}
                size="medium"
                placeholder="Select"
                text={selected?.label}
                panelLayout="overlay"
                open={open}
                onOpenChange={setOpen}
                className="cw-constraint__dropdown"
                items={options.map((o) => {
                    const isDisabled = Boolean(o.disabled) && o.value !== value;
                    return {
                        label: o.label,
                        state: o.value === value ? 'selected' : isDisabled ? 'disabled' : 'default',
                        onClick: () => {
                            if (isDisabled) return;
                            onChange(o.value);
                            setOpen(false);
                        },
                    };
                })}
            />
        </div>
    );
};

const withDisabledMetrics = (catalog, usedMetrics) =>
    catalog.map((m) => ({ ...m, disabled: usedMetrics.has(m.value) }));

/**
 * One editable constraint row ("Add Constraint", Figma 3099-9289 /
 * 3106-9825). Fields adapt to the selected constraint type:
 * performance (metric + aggregation + load profile), quality (value type +
 * evaluation metric + NER-only entity types/matching), cost (pricing metric)
 * and footprint (resource metric).
 */
const ConstraintRow = ({
    constraint,
    onChange,
    onRemove,
    isNerTask = false,
    entityTypeOptions = [],
    allConstraints = [],
    error = null,
}) => {
    const type = constraint.constraint_type;
    const valueMissing = constraint.value === '' || constraint.value === null;
    const usedMetrics = new Set(
        allConstraints
            .filter((c) => {
                if (c.id === constraint.id || c.constraint_type !== type) return false;
                if (type === 'performance') return c.aggregation === constraint.aggregation;
                return true;
            })
            .map((c) => c.metric)
    );
    const usedAggregations = new Set(
        allConstraints
            .filter(
                (c) =>
                    c.id !== constraint.id &&
                    c.constraint_type === 'performance' &&
                    c.metric === constraint.metric
            )
            .map((c) => c.aggregation)
    );
    const typeOptions = CONSTRAINT_TYPES.map((o) => ({
        ...o,
        disabled: unusedMetricsForType(allConstraints, o.value, constraint.id).length === 0,
    }));

    const update = (patch) => onChange({ ...constraint, ...patch });
    const updateMetric = (metric) => {
        const def = metricsForType(type).find((m) => m.value === metric);
        update({ metric, unit: def?.unit || constraint.unit });
    };

    const entityOptions = [
        { value: ALL_ENTITY_TYPES, label: 'All entity types' },
        ...entityTypeOptions.map((l) => ({ value: l, label: l })),
    ];

    const operatorAndValue = (
        <>
            <SelectField
                label={null}
                required={false}
                options={OPERATORS}
                value={constraint.operator}
                onChange={(operator) => update({ operator })}
                width={69}
            />
            <div className="cw-constraint__field cw-constraint__field--value">
                <div
                    className={`cw-constraint__value-input${
                        error && valueMissing ? ' cw-constraint__value-input--error' : ''
                    }`}
                >
                    {type === 'cost' && <span className="cw-constraint__value-unit">$</span>}
                    <input
                        type="number"
                        value={constraint.value}
                        onChange={(e) => update({ value: e.target.value })}
                        aria-label="Constraint value"
                    />
                    <span className="cw-constraint__value-unit">
                        {type === 'cost' ? '/hr' : constraint.unit}
                    </span>
                </div>
            </div>
        </>
    );

    return (
        <div className="cw-constraint__item">
            <div className="cw-constraint__row">
                <SelectField
                    label="Type"
                    options={typeOptions}
                    value={type}
                    onChange={(nextType) =>
                        onChange(retypeConstraint(constraint, nextType, allConstraints))
                    }
                    width={174}
                />

                {type === 'performance' && (
                    <>
                        <SelectField
                            label="Performance Metric"
                            options={withDisabledMetrics(PERFORMANCE_METRICS, usedMetrics)}
                            value={constraint.metric}
                            onChange={updateMetric}
                            width={200}
                        />
                        <SelectField
                            label="Aggregation"
                            options={AGGREGATIONS.map((o) => ({
                                ...o,
                                disabled: usedAggregations.has(o.value),
                            }))}
                            value={constraint.aggregation}
                            onChange={(aggregation) => update({ aggregation })}
                            width={148}
                        />
                        {operatorAndValue}
                        <span className="cw-constraint__at" aria-hidden="true">
                            @
                        </span>
                        <SelectField
                            label={null}
                            required={false}
                            options={LOAD_PROFILES}
                            value={constraint.load_profile}
                            onChange={(load_profile) => update({ load_profile })}
                            flex
                        />
                    </>
                )}

                {type === 'quality' && (
                    <>
                        <div className="cw-constraint__field cw-constraint__field--segmented">
                            <div className="cw-constraint__field-label">
                                Value Type<span className="cw-required-asterisk">*</span>
                            </div>
                            <SegmentedControl
                                size="medium"
                                items={QUALITY_VALUE_TYPES.map((o) => ({
                                    id: o.value,
                                    label: o.label,
                                    tooltip: o.tooltip,
                                }))}
                                activeId={constraint.value_type}
                                onChange={(value_type) => update({ value_type })}
                            />
                        </div>
                        <SelectField
                            label="Evaluation Metric"
                            options={withDisabledMetrics(EVALUATION_METRICS, usedMetrics)}
                            value={constraint.metric}
                            onChange={updateMetric}
                            width={120}
                        />
                        {isNerTask && (
                            <>
                                <SelectField
                                    label="Entity Types"
                                    options={entityOptions}
                                    value={constraint.entity_types?.[0] || ALL_ENTITY_TYPES}
                                    onChange={(entity) => update({ entity_types: [entity] })}
                                    flex
                                />
                                <SelectField
                                    label="Matching"
                                    options={MATCHING_OPTIONS}
                                    value={constraint.matching || MATCHING_OPTIONS[0].value}
                                    onChange={(matching) => update({ matching })}
                                    width={150}
                                />
                            </>
                        )}
                        {operatorAndValue}
                    </>
                )}

                {type === 'cost' && (
                    <>
                        <SelectField
                            label="Pricing Metric"
                            options={withDisabledMetrics(PRICING_METRICS, usedMetrics)}
                            value={constraint.metric}
                            onChange={updateMetric}
                            width={300}
                        />
                        {operatorAndValue}
                    </>
                )}

                {type === 'footprint' && (
                    <>
                        <SelectField
                            label="Resource Metric"
                            options={withDisabledMetrics(RESOURCE_METRICS, usedMetrics)}
                            value={constraint.metric}
                            onChange={updateMetric}
                            width={300}
                        />
                        {operatorAndValue}
                    </>
                )}

                <Button
                    variant="ghost"
                    size="medium"
                    icon={<SparkIcon slug="trash" size="small" />}
                    onClick={onRemove}
                    aria-label="Remove constraint"
                    className="cw-constraint__remove"
                />
            </div>
            {error && (
                <div className="cw-field-error" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
};

export default ConstraintRow;
